import { z } from 'zod';

import {
  actionPropertiesSchema,
  experimentAssignedPropertiesSchema,
  experimentExposedPropertiesSchema,
  experimentOutcomePropertiesSchema,
  featureFlagEvaluatedPropertiesSchema,
  outcomePropertiesSchema,
  viewPropertiesSchema,
  type ActionPropertiesInput,
  type ExperimentAssignedPropertiesInput,
  type ExperimentExposedPropertiesInput,
  type ExperimentOutcomePropertiesInput,
  type FeatureFlagEvaluatedPropertiesInput,
  type OutcomePropertiesInput,
  type ViewPropertiesInput,
} from './properties';
import {
  LEGACY_PARITY_PROPERTIES_SCHEMAS,
  type LegacyParityEventName,
  type LegacyParityPropertiesInput,
} from './legacy-parity';

// PostHog event names are lowercase snake_case by convention and can never be
// renamed once shipped; a shared dotted prefix would also rank the canonical
// events worse in the insight-builder's length-ordered search.
export const PRODUCT_EVENT_NAMES = [
  'screen_viewed',
  'user_action',
  'server_outcome',
  'feature_flag_evaluated',
  'experiment_assigned',
  'experiment_exposed',
  'experiment_outcome',
] as const;

export const productEventNameSchema = z.enum(PRODUCT_EVENT_NAMES);
export type ProductEventName = z.infer<typeof productEventNameSchema>;
export const DOMAIN_EVENT_NAME_PATTERN = '^[a-z][a-z0-9]*(?:\\.[a-z][a-z0-9]*){2,}$' as const;
export const domainEventNameSchema = z
  .string()
  .min(5)
  .max(200)
  .regex(new RegExp(DOMAIN_EVENT_NAME_PATTERN))
  .refine(
    (value) => !PRODUCT_EVENT_NAMES.some((productEventName) => productEventName === value),
    'Canonical product events must use their typed schema',
  );
export type DomainEventName = z.infer<typeof domainEventNameSchema>;

export const STANDALONE_PRODUCT_EVENT_NAME_PATTERN =
  '^[a-z][a-z0-9_]*:[a-z][a-z0-9_]*_[a-z][a-z0-9_]*$' as const;
export const standaloneProductEventNameSchema = z
  .string()
  .min(5)
  .max(200)
  .regex(new RegExp(STANDALONE_PRODUCT_EVENT_NAME_PATTERN));

// Standalone product events join authenticated activity only after they have a
// reviewed registry entry. No such event is currently emitted by Galapagos.
export const REGISTERED_STANDALONE_PRODUCT_EVENT_NAMES = Object.freeze(
  standaloneProductEventNameSchema.array().parse([]),
);

const captureOptionsSchema = z
  .object({
    timestamp: z
      .union([z.date(), z.string().datetime()])
      .transform((value) => (value instanceof Date ? value.toISOString() : value))
      .optional(),
    insertId: z
      .string()
      .min(1)
      .max(200)
      .regex(/^[A-Za-z0-9][A-Za-z0-9:._-]*$/)
      .optional(),
    processPersonProfile: z.boolean().optional(),
    // Server-stamped instants (e.g. the member_local_timestamp contract) must
    // survive vendor ingestion untouched; providers map this to their
    // ignore-sent-at mechanism so clock-skew correction never rewrites the
    // supplied timestamp.
    preserveTimestamp: z.boolean().optional(),
  })
  .strict();

export type CaptureOptionsInput = z.input<typeof captureOptionsSchema>;
export type CaptureOptions = z.output<typeof captureOptionsSchema>;

function eventSchema<Name extends ProductEventName, Properties extends z.ZodTypeAny>(
  name: Name,
  properties: Properties,
) {
  return z
    .object({
      event: z.literal(name),
      properties,
      options: captureOptionsSchema.optional(),
    })
    .strict();
}

const viewEventSchema = eventSchema('screen_viewed', viewPropertiesSchema);
const actionEventSchema = eventSchema('user_action', actionPropertiesSchema);
const outcomeEventSchema = eventSchema('server_outcome', outcomePropertiesSchema);
const featureFlagEvaluatedEventSchema = eventSchema(
  'feature_flag_evaluated',
  featureFlagEvaluatedPropertiesSchema,
);
const experimentAssignedEventSchema = eventSchema(
  'experiment_assigned',
  experimentAssignedPropertiesSchema,
);
const experimentExposedEventSchema = eventSchema(
  'experiment_exposed',
  experimentExposedPropertiesSchema,
);
const experimentOutcomeEventSchema = eventSchema(
  'experiment_outcome',
  experimentOutcomePropertiesSchema,
);

const canonicalProductEventSchema = z.discriminatedUnion('event', [
  viewEventSchema,
  actionEventSchema,
  outcomeEventSchema,
  featureFlagEvaluatedEventSchema,
  experimentAssignedEventSchema,
  experimentExposedEventSchema,
  experimentOutcomeEventSchema,
]);

const domainProductEventSchema = z
  .object({
    event: domainEventNameSchema,
    properties: z.record(z.string(), z.unknown()),
    options: captureOptionsSchema.optional(),
  })
  .strict();

function legacyParityEventSchema<Name extends LegacyParityEventName>(name: Name) {
  // Validated against the event's strict schema but typed as a plain record, so
  // code reading `ProductEvent['properties']` keeps its record-shaped access.
  const properties = z
    .record(z.string(), z.unknown())
    .refine((value) => LEGACY_PARITY_PROPERTIES_SCHEMAS[name].safeParse(value).success, {
      message: `Properties do not match the ${name} schema`,
    });
  return z
    .object({
      event: z.literal(name),
      properties,
      options: captureOptionsSchema.optional(),
    })
    .strict();
}

// Exact production names only; the domain grammar is lowercase, so the two
// families can never claim the same event.
const legacyParityProductEventSchema = z.discriminatedUnion('event', [
  legacyParityEventSchema('Growth.RegFlow.Viewed'),
  legacyParityEventSchema('Growth.RegFlow.EmployerSearched'),
  legacyParityEventSchema('Growth.RegFlow.MemberRegistered'),
  legacyParityEventSchema('Growth.RegFlow.QuizCompleted'),
]);

export const productEventSchema = z.union([
  canonicalProductEventSchema,
  domainProductEventSchema,
  legacyParityProductEventSchema,
]);

export type ProductEvent = z.infer<typeof productEventSchema>;

function buildEvent(
  event: ProductEventName,
  properties: unknown,
  options?: CaptureOptionsInput,
): ProductEvent {
  return productEventSchema.parse({ event, properties, options });
}

export const buildViewEvent = (properties: ViewPropertiesInput, options?: CaptureOptionsInput) =>
  buildEvent('screen_viewed', properties, options);
export const buildActionEvent = (
  properties: ActionPropertiesInput,
  options?: CaptureOptionsInput,
) => buildEvent('user_action', properties, options);
export const buildOutcomeEvent = (
  properties: OutcomePropertiesInput,
  options?: CaptureOptionsInput,
) => buildEvent('server_outcome', properties, options);
export const buildFeatureFlagEvaluatedEvent = (
  properties: FeatureFlagEvaluatedPropertiesInput,
  options?: CaptureOptionsInput,
) => buildEvent('feature_flag_evaluated', properties, options);
export const buildExperimentAssignedEvent = (
  properties: ExperimentAssignedPropertiesInput,
  options?: CaptureOptionsInput,
) => buildEvent('experiment_assigned', properties, options);
export const buildExperimentExposedEvent = (
  properties: ExperimentExposedPropertiesInput,
  options?: CaptureOptionsInput,
) => buildEvent('experiment_exposed', properties, options);
export const buildExperimentOutcomeEvent = (
  properties: ExperimentOutcomePropertiesInput,
  options?: CaptureOptionsInput,
) => buildEvent('experiment_outcome', properties, options);

export const buildDomainEvent = (
  event: DomainEventName,
  properties: Record<string, unknown>,
  options?: CaptureOptionsInput,
): ProductEvent => productEventSchema.parse({ event, properties, options });

export const buildLegacyParityEvent = <Name extends LegacyParityEventName>(
  event: Name,
  properties: LegacyParityPropertiesInput<Name>,
  options?: CaptureOptionsInput,
): ProductEvent => productEventSchema.parse({ event, properties, options });
